import { Heading } from '@music-vine/cadence/ui';
import { useRouter } from 'next/router';
import { memo, useMemo, useRef } from 'react';
import { FreeFirstNoFreeResults } from '@/components/FreeFirstNoFreeResults';
import { mgHitToTile } from '@/components/MGTile/mg-hit-to-tile';
import { NoResults } from '@/components/NoResults';
import { useFreeFirstMotionGraphicsTier } from '@/hooks/search/useFreeFirstMotionGraphicsTier';
import { MGProvider } from '@/providers/MGProvider';
import { toLoggableAssetType, trackSectionShowMore } from '@/utils/analytics/showFreeFirst';
import { getPageType } from '@/utils/tracking';
import type { MotionGraphicsTierResult } from '@/hooks/search/useFreeFirstMotionGraphicsTier';
import type { ReactNode } from 'react';
import type { SectionAssetType } from '@/utils/show-free-first/sectionCopy';
import type { SortOption } from '@music-vine/setlist';
interface TierArgs {
  filter: string;
  q?: string;
  sort: SortOption;
}
const isTierVisible = (tier: MotionGraphicsTierResult) => tier.isLoading || tier.hits.length > 0;
const isTierResolvedEmpty = (tier: MotionGraphicsTierResult) => !(tier.isLoading || tier.isError) && tier.hits.length === 0;
const SKELETON_CELL_KEYS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
const skeletonCells = SKELETON_CELL_KEYS.map(id => <div aria-hidden="true" key={`mg-skeleton-cell-${id}`} />);
const TierGrid = ({
  assetType,
  isLut,
  section,
  tier
}: {
  assetType: SectionAssetType;
  isLut: boolean;
  section: 'free' | 'premium';
  tier: MotionGraphicsTierResult;
}) => {
  const {
    asPath
  } = useRouter();
  const isInitialLoading = tier.isLoading && tier.hits.length === 0;
  const nextPageIndex = useRef(1);
  const tiles = useMemo<ReactNode[]>(() => tier.hits.map(hit => hit && 'document' in hit ? mgHitToTile(hit, {
    isLut
  }) : null).filter(Boolean), [tier.hits, isLut]);
  const gridChildren = isInitialLoading ? skeletonCells : tiles;
  return <MGProvider discretePaging fetchNextPage={tier.fetchNextPage}
  // Suppress the load-more affordance while the skeleton is showing.
  hasNextPage={!isInitialLoading && tier.hasNextPage} isFetchingNextPage={tier.isFetchingNextPage} onShowMoreClick={() => {
    nextPageIndex.current += 1;
    trackSectionShowMore(section, {
      asPath,
      assetType: toLoggableAssetType(assetType),
      pageType: getPageType(asPath)?.contentType,
      pageIndex: nextPageIndex.current
    });
  }} showMoreLabel={section === 'free' ? 'Show more free' : 'Show more premium'} data-sentry-element="MGProvider" data-sentry-component="TierGrid" data-sentry-source-file="FreeFirstMotionGraphicsSections.tsx">
      {gridChildren}
    </MGProvider>;
};
interface FreeFirstMotionGraphicsSectionsProps extends TierArgs {
  assetType: SectionAssetType;
  // LUTs share this listing but render LUT-styled tiles (no compatibility badge).
  isLut?: boolean;
  premiumHeading: string;
}
export const FreeFirstMotionGraphicsSections = memo(({
  assetType,
  isLut = false,
  premiumHeading,
  ...tierArgs
}: FreeFirstMotionGraphicsSectionsProps) => {
  const free = useFreeFirstMotionGraphicsTier(false, tierArgs);
  const premium = useFreeFirstMotionGraphicsTier(true, tierArgs);
  if (isTierResolvedEmpty(free) && isTierResolvedEmpty(premium)) {
    return <NoResults />;
  }

  // Premium matches but no free tiles: surface an empty state above the premium
  // picks rather than silently dropping into a premium-only grid.
  const showFreeEmptyState = isTierResolvedEmpty(free);
  return <div className="flex flex-col gap-12">
        {isTierVisible(free) ? <TierGrid assetType={assetType} isLut={isLut} section="free" tier={free} /> : null}
        {showFreeEmptyState ? <FreeFirstNoFreeResults assetType={assetType} /> : null}
        {isTierVisible(premium) ? <div>
            <Heading className="mb-4 font-aeonik @md:text-2xl text-2xl" fontFamily={null} variant="h5">
              {premiumHeading}
            </Heading>
            <TierGrid assetType={assetType} isLut={isLut} section="premium" tier={premium} />
          </div> : null}
      </div>;
});
FreeFirstMotionGraphicsSections.displayName = 'FreeFirstMotionGraphicsSections';